import {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {createSelector} from 'reselect';
import {makeLayoutQueryKey} from 'src/modules/profile/user/tabs/content/layouts/util';
import {getUserLayouts} from 'src/openapi/admin/apis/UserLayoutsApi';
import {UserLayout} from 'src/openapi/admin/models/UserLayout';
import {useAppDispatch, useAppSelector} from 'src/store/hooks';

import {
    makeSelectLayoutsByKey,
    selectReceivedAt,
} from '../slices/layoutsSelectors';
import {LayoutsPageState, defaultLayoutCriteria, invalidateQuery} from '../slices/layoutsSlice';

import {useLayoutCriteria} from './useLayoutCriteria';

type Props = {
    scopeKey: string,
    userId: number,
    autoload?: boolean,
    ttlMs?: number,
};

export function useUserLayouts({scopeKey, userId, autoload = true, ttlMs = 120_000}: Props) {
    const dispatch = useAppDispatch();
    const sliceDispatch = useDispatch();
    const {criteria, setCriteria, setPage, setSort, reset, setPageSize} = useLayoutCriteria(scopeKey);

    useEffect(() => {
        if (!criteria) {
            setCriteria(defaultLayoutCriteria);
        }
    }, [criteria, setCriteria]);

    const queryKey = useMemo(
        () => (criteria ? makeLayoutQueryKey(userId, criteria) : ''),
        [userId, criteria],
    );

    const selectLayouts = useMemo(
        () =>
            queryKey
                ? makeSelectLayoutsByKey(queryKey)
                : createSelector([], () => ({
                        items: [] as UserLayout[],
                        total: 0,
                        status: 'idle' as const,
                        error: undefined,
                    })),
        [queryKey],
    );

    const {items, total, status, error} = useAppSelector(selectLayouts);

    const refetch = useCallback(() => {
        if (!criteria) {
            return;
        }
        dispatch(getUserLayouts({userId, userLayoutRequest: criteria}));
    }, [dispatch, userId, criteria]);

    const invalidate = useCallback(() => {
        if (queryKey) {
            sliceDispatch(invalidateQuery(queryKey));
        }
    }, [sliceDispatch, queryKey]);

    const receivedAt = useAppSelector(
        (state: LayoutsPageState) => selectReceivedAt(state, queryKey),
    );

    useEffect(() => {
        if (!autoload || !criteria) {
            return;
        }

        if (status === 'idle' || status === 'failed') {
            refetch();
            return;
        }

        if (status === 'succeeded' && receivedAt) {
            const now = Date.now();
            if (now - receivedAt > ttlMs) {
                refetch();
            }
        }
    }, [autoload, criteria, status, queryKey, refetch, receivedAt, ttlMs]);

    return {
        data: items,
        total,
        status,
        error,
        criteria,
        setCriteria,
        setPage,
        setPageSize,
        setSort,
        resetCriteria: reset,
        refetch,
        invalidate,
        isFetching: status === 'loading',
    };
}
